import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { studio } from "@/data/site";

const links = [
  { to: "/apps", label: "Apps", id: "apps" },
  { to: "/support", label: "Support", id: "support" },
  { to: "/sources", label: "Sources", id: "sources" },
  { to: "/privacy", label: "Privacy", id: "privacy" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open ? "glass" : "border-b border-transparent"
      }`}
    >
      <div className="shell flex h-16 items-center justify-between sm:h-20">
        <Link
          to="/"
          data-testid="navbar-brand-logo"
          className="group flex items-center gap-2.5"
          aria-label={`${studio.name} home`}
        >
          <span
            aria-hidden
            className="h-2.5 w-2.5 rounded-full transition-transform duration-500 group-hover:scale-150"
            style={{ background: "var(--accent)", boxShadow: "0 0 18px var(--accent)" }}
          />
          <span className="font-display text-lg font-extrabold tracking-tight text-white sm:text-xl">
            {studio.displayName}
          </span>
        </Link>

        <nav className="hidden items-center gap-9 md:flex" aria-label="Main">
          {links.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `link-underline text-sm transition-colors duration-300 ${
                  isActive ? "text-white" : "text-gray-400 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/support"
            data-testid="nav-cta-contact"
            className="rounded-full px-5 py-2.5 text-sm font-medium text-[#08090d] transition-transform duration-300 hover:-translate-y-0.5"
            style={{ background: "var(--accent)" }}
          >
            Get help
          </Link>
        </nav>

        <button
          type="button"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          className="md:hidden rounded-full border p-2.5 text-white"
          style={{ borderColor: "var(--line)" }}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden md:hidden"
            aria-label="Mobile"
          >
            <div className="shell flex flex-col gap-1 pb-6 pt-2">
              {links.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className="border-b py-4 text-base text-gray-200"
                  style={{ borderColor: "var(--line)" }}
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/support"
                data-testid="nav-mobile-cta"
                className="mt-4 rounded-full px-5 py-3 text-center text-sm font-medium text-[#08090d]"
                style={{ background: "var(--accent)" }}
              >
                Get help
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
